import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import TooltipIcon from '../../../../../../../../components/tooltip-icon/TooltipIcon';
import { useGetCertificatePermissionsById } from '../../../../../../../../hooks/api/certificates';
import { Locale } from '../../../../../../../../i18n';
import {
  OPTIMISTIC_CERTIFICATE_PERMISSIONS_QUERY_KEY,
  pruneExpiredOptimisticCertificatePermissions,
  readOptimisticCertificatePermissions,
} from '../../../../../../../../shared/helpers/optimistic-certificate-permissions';
import { parseDateWithoutTime } from '../../../../../../../../shared/helpers/parse-date';
import {
  CertificatePermissionReviewStatus,
  CertificatePermissionType,
  ICertificatePermission,
} from '../../../../../../../../shared/interfaces/ICertificate';
import styles from './CertificatePermissionsTable.module.css';
import CertificatePermissionStatus from './CertificatePermissionStatus';
import WithdrawCertificateButton from './WithdrawCertificateButton';

type CertificatePermissionsTableProps = {
  certificateId?: string;
};

// A pending row may not be persisted yet — no id and no review status — so the table tracks rows by
// a synthetic `rowKey` and skeletons the columns whose values only the server knows. `isPending`
// applies to real rows too: the API writes the permission row before the EDC exchange runs, so the
// row existing does not mean the grant has finished.
type CertificatePermissionRow = Omit<ICertificatePermission, 'id' | 'consumerReviewStatus'> & {
  id?: number;
  consumerReviewStatus?: CertificatePermissionReviewStatus;
  rowKey: string;
  isPending?: boolean;
};

const CertificatePermissionsTable = ({
  certificateId,
}: CertificatePermissionsTableProps): JSX.Element => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const queryClient = useQueryClient();
  const { data: permissions = [], isLoading } = useGetCertificatePermissionsById(
    { certificateId: certificateId ?? '' },
    {
      enabled: !!certificateId,
    },
  );

  // Pending grants live in a dedicated cache entry backed by sessionStorage (see
  // optimistic-certificate-permissions.ts) so neither the blanket ['certificate'] invalidations nor
  // a page reload wipe them. Reading them through useQuery hydrates the cache from storage on mount
  // and is what makes the table re-render on setQueryData.
  const { data: optimisticPermissions = [] } = useQuery({
    queryKey: OPTIMISTIC_CERTIFICATE_PERMISSIONS_QUERY_KEY,
    queryFn: readOptimisticCertificatePermissions,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  // Expiry is driven from here rather than from the dialog that started the grant, so that entries
  // restored from storage after a reload — where the original timer is gone — still expire.
  useEffect(() => {
    if (!optimisticPermissions.length) {
      return;
    }

    const soonestExpiry = Math.min(
      ...optimisticPermissions.map((row) => {
        return row.expiresAt;
      }),
    );

    // The small margin keeps the timer from firing a hair early, which would prune nothing and
    // leave the entry sitting there until the next cache change re-armed this effect.
    const timeoutId = window.setTimeout(
      () => {
        pruneExpiredOptimisticCertificatePermissions(queryClient);
      },
      Math.max(soonestExpiry - Date.now(), 0) + 1000,
    );

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [optimisticPermissions, queryClient]);

  const tableRows = useMemo<CertificatePermissionRow[]>(() => {
    const pendingGrants = optimisticPermissions.filter((row) => {
      return row.certificateId === certificateId;
    });

    // Granting access (Pull) and sharing (Push) are separate permissions, so a row is only matched
    // to a pending grant of the same type.
    const getMatchKey = (bpn: string, type: CertificatePermissionType): string => {
      return `${bpn}|${type}`;
    };

    const pendingMatchKeys = new Set(
      pendingGrants.map((row) => {
        return getMatchKey(row.consumerCompanyBpn, row.type);
      }),
    );

    const realRows = permissions.map((permission) => {
      return {
        ...permission,
        rowKey: String(permission.id),
        // The permission row is written before the EDC exchange runs, so it existing tells us
        // nothing about whether the grant finished. Keep the skeletons until this company's own
        // websocket message arrives — another company's failure must not clear them.
        isPending: pendingMatchKeys.has(
          getMatchKey(permission.consumerCompanyBpn, permission.type),
        ),
      };
    });

    const persistedMatchKeys = new Set(
      permissions.map((permission) => {
        return getMatchKey(permission.consumerCompanyBpn, permission.type);
      }),
    );

    // Until the permission row has been fetched there is nothing to attach the pending state to, so
    // the grant is rendered as a standalone placeholder row.
    const placeholderRows = pendingGrants
      .filter((row) => {
        return !persistedMatchKeys.has(getMatchKey(row.consumerCompanyBpn, row.type));
      })
      .map((row) => {
        return { ...row, rowKey: `optimistic-${row.optimisticKey}`, isPending: true };
      });

    return [...placeholderRows, ...realRows];
  }, [permissions, optimisticPermissions, certificateId]);

  // A pending grant is still being exchanged, so its not-yet-known columns show a skeleton.
  const pendingCellSkeleton = <Skeleton height='1rem' width='80%' />;

  const companyTemplate = (rowData: CertificatePermissionRow): React.ReactNode => {
    return (
      <div className='flex flex-column gap-1'>
        <span>{rowData.consumerCompanyName}</span>
        <span className='text-600 text-xs'>{rowData.consumerCompanyBpn}</span>
      </div>
    );
  };

  const typeTemplate = (rowData: CertificatePermissionRow): React.ReactNode => {
    if (rowData.type === CertificatePermissionType.PUSH) {
      return (
        <TooltipIcon
          id={`certificate-type-pushed-${rowData.rowKey}`}
          tooltipText={t('certificateDetailsPage.tabs.permissionsTab.types.pushed')}
          icon={<i className='pi pi-send text-gray-700' />}
          width='fit'
        />
      );
    }

    return (
      <TooltipIcon
        id={`certificate-type-access-granted-${rowData.rowKey}`}
        tooltipText={t('certificateDetailsPage.tabs.permissionsTab.types.accessGranted')}
        icon={<i className='pi pi-eye text-gray-700' />}
        width='fit'
      />
    );
  };

  const dateSharedTemplate = (rowData: CertificatePermissionRow): React.ReactNode => {
    return parseDateWithoutTime(rowData.dateCreated, language as Locale);
  };

  const sharedByTemplate = (rowData: CertificatePermissionRow): React.ReactNode => {
    const firstName = rowData.approvedByUser?.firstName || '';
    const lastName = rowData.approvedByUser?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || '-';
  };

  const lastRetrievedTemplate = (rowData: CertificatePermissionRow): React.ReactNode => {
    if (rowData.isPending) {
      return pendingCellSkeleton;
    }

    if (!rowData.lastRetrieved) {
      return (
        <span className='font-light font-italic text-gray-700'>
          {t('certificateDetailsPage.tabs.permissionsTab.notYet')}
        </span>
      );
    }

    return parseDateWithoutTime(rowData.lastRetrieved, language as Locale);
  };

  const statusTemplate = (rowData: CertificatePermissionRow): React.ReactNode => {
    if (rowData.isPending) {
      // Nothing to withdraw while the exchange is still running — and a placeholder row has no id
      // to withdraw against in the first place.
      return pendingCellSkeleton;
    }

    if (rowData.id && !rowData.lastRetrieved && rowData.type === CertificatePermissionType.PULL) {
      // If it is a PULL entry and has not been retrieved yet, show the Withdraw button
      return <WithdrawCertificateButton permissionId={rowData.id} certificateId={certificateId} />;
    }

    return <CertificatePermissionStatus status={rowData.consumerReviewStatus} />;
  };

  return (
    <div className={styles['certificate-permissions-table']}>
      <DataTable
        value={tableRows}
        loading={isLoading}
        dataKey='rowKey'
        scrollable
        tableStyle={{
          tableLayout: 'fixed',
          minWidth: '900px',
        }}
        emptyMessage={t('global.emptyTableMessage')}
        sortField='dateCreated'
        sortOrder={-1}
      >
        <Column
          header={t(
            'certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.companyName',
          )}
          body={companyTemplate}
          style={{ width: '28%', wordBreak: 'break-word' }}
        />

        <Column
          header={t('certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.type')}
          body={typeTemplate}
          style={{ width: '8%' }}
        />

        <Column
          header={t(
            'certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.dateShared',
          )}
          body={dateSharedTemplate}
          style={{ width: '16%' }}
        />

        <Column
          header={t('certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.sharedBy')}
          body={sharedByTemplate}
          style={{ width: '18%' }}
        />

        <Column
          header={t(
            'certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.lastRetrieved',
          )}
          body={lastRetrievedTemplate}
          style={{ width: '16%' }}
        />

        <Column
          header={t('certificateDetailsPage.tabs.permissionsTab.permissionsTableColumns.status')}
          body={statusTemplate}
          style={{ width: '14%' }}
        />
      </DataTable>
    </div>
  );
};

export default CertificatePermissionsTable;
